import React, { useState, useEffect } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";

// Cache global de la clé API pour éviter des requêtes répétées vers le backend
let cachedApiKeyPromise = null;

const fetchApiKey = () => {
    if (!cachedApiKeyPromise) {
        const apiBaseUrl = process.env.REACT_APP_BACKEND_URL || "";
        cachedApiKeyPromise = axios
            .get(`${apiBaseUrl}/api/api-football-key`)
            .then((res) => res.data.key)
            .catch((err) => {
                cachedApiKeyPromise = null; // Réinitialise en cas d'erreur
                throw err;
            });
    }
    return cachedApiKeyPromise;
};

export const ApiFootballWidget = ({ type, league, season, ...props }) => {
    const [apiKey, setApiKey] = useState(null);
    const [scriptLoaded, setScriptLoaded] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        let active = true;

        // 1. Charger la clé API du backend
        fetchApiKey()
            .then((key) => {
                if (active) setApiKey(key);
            })
            .catch((err) => {
                if (active) {
                    console.error("Failed to load API Football Key:", err);
                    setError("Impossible de charger la configuration de l'API.");
                }
            });

        // 2. Charger le script API-Football
        const scriptSrc = "https://widgets.api-sports.io/3.1.0/widgets.js";
        let script = document.querySelector(`script[src="${scriptSrc}"]`);

        if (!script) {
            script = document.createElement("script");
            script.type = "module";
            script.src = scriptSrc;
            script.async = true;
            document.body.appendChild(script);
        }

        const handleScriptLoad = () => {
            if (active) setScriptLoaded(true);
        };

        const handleScriptError = () => {
            if (active) {
                console.error("Failed to load API Football widget script.");
                setError("Impossible de charger les widgets de statistiques (vérifiez si votre bloqueur de publicité bloque widgets.api-sports.io).");
            }
        };

        // Si le script est déjà chargé (ou s'il se charge)
        if (script.complete || window.customElements?.get("api-sports-widget")) {
            setScriptLoaded(true);
        } else {
            script.addEventListener("load", handleScriptLoad);
            script.addEventListener("error", handleScriptError);
        }

        return () => {
            active = false;
            if (script) {
                script.removeEventListener("load", handleScriptLoad);
                script.removeEventListener("error", handleScriptError);
            }
        };
    }, []);

    if (error) {
        return (
            <div className="card-elev p-6 text-center border-l-4 border-l-[#E63946]">
                <p className="text-[#E63946] text-sm font-semibold">{error}</p>
            </div>
        );
    }

    if (!apiKey || !scriptLoaded) {
        return (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400 text-sm gap-3">
                <Loader2 className="animate-spin text-[#00C853]" size={28} />
                <span>Chargement du widget de statistiques live...</span>
            </div>
        );
    }

    return (
        <div className="api-football-widget-container animate-fade-in relative min-h-[300px]">
            {/* Widget de Configuration Global (requis une seule fois par page) */}
            <api-sports-widget
                data-type="config"
                data-key={apiKey}
                data-sport="football"
                data-theme="pronospot"
            ></api-sports-widget>

            {/* Widget de Contenu Spécifique (Classement ou Matchs) */}
            <api-sports-widget
                data-type={type}
                data-league={league}
                data-season={season}
                data-theme="pronospot"
                {...props}
            ></api-sports-widget>
        </div>
    );
};

export default ApiFootballWidget;
